import { convertToINR } from "../../../helpers/Utils";

export function getUTRString(payment) {
  const { payment_type = "payment", invoice_no = "", utr = "NA" } = payment;
  return `UTR No. for ${payment_type} - ${invoice_no}: ${utr} `;
}

/**
 * Payment lines for one invoice, from the PO details `payments[]` array.
 *
 * Matched by invoice_no only. Legacy CSV rows carried payment_type "Invoice";
 * v2 lines (ENG-27224) carry "RUNNING" / "FINAL". Advance lines have no
 * invoice_no, so they never match. Never filter on payment_type here.
 */
export function paymentsForInvoice(payments, invoiceNo) {
  if (!Array.isArray(payments) || !invoiceNo) return [];
  return payments.filter((p) => p?.invoice_no === invoiceNo);
}

/**
 * The most recent payment line for an invoice (by payment_date), or null.
 * Drives the "[date]" shown under Amount Paid on the PO page.
 */
export function latestInvoicePayment(payments, invoiceNo) {
  const lines = paymentsForInvoice(payments, invoiceNo);
  if (lines.length === 0) return null;
  return lines.reduce((latest, p) => {
    const t = Date.parse(p?.payment_date ?? "");
    const lt = Date.parse(latest?.payment_date ?? "");
    if (Number.isNaN(t)) return latest;
    if (Number.isNaN(lt)) return p;
    return t > lt ? p : latest;
  });
}

/**
 * Whether the PO's invoices have used more advance than was ever paid to the
 * vendor (ENG-28382). Paid advance = the PO's advance lines in `payments[]`
 * (legacy "Advance", v2 "ADVANCE"); used = Σ invoices[].advance_adjusted.
 *
 * VIP credits the advance when the PO is acknowledged, before it is paid, so an
 * invoice can consume it while the advance request is still unpaid. A partly
 * paid advance cannot be apportioned to one invoice, so a shortfall marks every
 * invoice on the PO that used advance. Compared to the paisa.
 */
export function isAdvancePending(po) {
  const used = (po?.invoices ?? []).reduce((sum, inv) => sum + (inv?.advance_adjusted ?? 0), 0);
  const paid = (po?.payments ?? [])
    .filter((p) => (p?.payment_type ?? "").toLowerCase() === "advance")
    .reduce((sum, p) => sum + (p?.amount ?? 0), 0);
  return Math.round((used - paid) * 100) > 0;
}

/**
 * "₹X (N Payments)" summary shown on the PO page, revised-PO review and
 * credit-note flow. `totalPaid` is the backend's `total_amount_paid`, which is
 * already the union of legacy and v2 payments (ENG-27224).
 */
export function paymentDetailsText(totalPaid, paymentCount) {
  if (!paymentCount) return "No payments made";
  return `${convertToINR(totalPaid ?? 0)} (${paymentCount} Payment${paymentCount > 1 ? "s" : ""})`;
}
